import React from 'react';
import { useLens } from './LensProvider';
import { useNarrative } from '../narrative/NarrativeProvider';
import Tooltip from './Tooltip';

const SUBTITLES = {
  B: 'How much intensity can your body hold right now?',
  S: 'How much are your senses taking in?',
  N: 'How fixed are your interpretations?',
  'I.openness': 'Willingness to let experience arise without resistance',
  'I.fear': 'A natural protective response to uncertainty',
  'I.care': 'Warmth and connection toward what arises',
  'I.control': 'The impulse to manage or direct experience',
};

const Slider = ({ label, subtitle, value, onChange }) => (
  <div className="slider-group">
    <label>
      {label}
      {subtitle && <span className="slider-subtitle">{subtitle}</span>}
    </label>
    <div className="slider-row">
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      <span className="slider-value">{value.toFixed(2)}</span>
    </div>
  </div>
);

export const Controls = () => {
  const { tooltips } = useNarrative();
  const {
    B, setB,
    S, setS,
    N, setN,
    I, setI,
    P, setP,
    projectionModes,
  } = useLens();

  const handleIntentChange = (key, value) => {
    setI((prevI) => ({ ...prevI, [key]: value }));
  };

  const formatTooltip = (tooltip) => (
    <span>
      {tooltip.text}
      <br />
      <em>{tooltip.not}</em>
    </span>
  );

  return (
    <div className="controls">
      <h2>Lens Controls</h2>
      <p className="controls-analogy">These shape the aperture — like adjusting a camera's opening to let in more or less light.</p>
      <Tooltip content={formatTooltip(tooltips.B)}>
        <Slider label={tooltips.B.label} subtitle={SUBTITLES.B} value={B} onChange={setB} />
      </Tooltip>
      <Tooltip content={formatTooltip(tooltips.S)}>
        <Slider label={tooltips.S.label} subtitle={SUBTITLES.S} value={S} onChange={setS} />
      </Tooltip>
      <Tooltip content={formatTooltip(tooltips.N)}>
        <Slider label={tooltips.N.label} subtitle={SUBTITLES.N} value={N} onChange={setN} />
      </Tooltip>

      <h3>Intent</h3>
      <p className="controls-analogy">Like tuning a radio — you don't change what's broadcasting, just what you receive.</p>
      <Tooltip content={formatTooltip(tooltips['I.openness'])}>
        <Slider label={tooltips['I.openness'].label} subtitle={SUBTITLES['I.openness']} value={I.openness} onChange={(v) => handleIntentChange('openness', v)} />
      </Tooltip>
      <Tooltip content={formatTooltip(tooltips['I.fear'])}>
        <Slider label={tooltips['I.fear'].label} subtitle={SUBTITLES['I.fear']} value={I.fear} onChange={(v) => handleIntentChange('fear', v)} />
      </Tooltip>
      <Tooltip content={formatTooltip(tooltips['I.care'])}>
        <Slider label={tooltips['I.care'].label} subtitle={SUBTITLES['I.care']} value={I.care} onChange={(v) => handleIntentChange('care', v)} />
      </Tooltip>
      <Tooltip content={formatTooltip(tooltips['I.control'])}>
        <Slider label={tooltips['I.control'].label} subtitle={SUBTITLES['I.control']} value={I.control} onChange={(v) => handleIntentChange('control', v)} />
      </Tooltip>

      <h3>Projection Mode</h3>
      <p className="controls-analogy">Same scene, different film stock — the experience is identical, the rendering changes.</p>
      <select value={P} onChange={(e) => setP(e.target.value)}>
        {projectionModes.map((mode) => (
          <option key={mode} value={mode}>
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </option>
        ))}
      </select>
    </div>
  );
};
